import axios from 'axios';
import { getIdentityToken } from '@/services/http';
import List from './list';

// Chat session structure from history API
export interface ChatSession {
  session_id: string;
  title: string;
  agent_name: string;
  agent_id: string;
  agent_avatar?: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview?: string;
}

// Individual message structure for session history
export interface ChatHistoryMessage {
  id?: string; // Optional since backend might not provide it
  role: 'user' | 'assistant';
  content: string;
  timestamp: number; // Unix timestamp from backend
  metadata?: any;
}

// API request structure for history search
export interface ChatHistorySearchRequest {
  q?: string;  // search query string
  limit?: number;  // pagination limit
  agent_id?: string;  // filter by specific agent
  authorization?: string;  // header for auth (handled separately)
}

// API response structure for search results
export interface ChatHistorySearchResponse {
  sessions: ChatSession[];
  total_count: number;
  query: string;
}

// API response structure for session detail
export interface ChatSessionDetailResponse {
  session_id: string;
  messages: ChatHistoryMessage[];
  agent_id: string;
  agent_name: string;
}

// API error response structure
export interface ChatHistoryApiError {
  message: string;
  code?: string;
  details?: any;
}

export default class ChatHistoryCollection extends List<ChatSession> {
  private cache: Map<string, ChatSessionDetailResponse> = new Map();
  private lastSearchQuery: string = '';
  private totalCount: number = 0;

  /**
   * Search chat history using the /history/search endpoint
   *
   * @param searchRequest - The search parameters
   * @returns Promise<ChatHistorySearchResponse> - The search results
   */
  public async searchHistory(searchRequest: ChatHistorySearchRequest = {}): Promise<ChatHistorySearchResponse> {
    try {
      const token = this.getAuthToken();
      const baseURL = this.getBaseURL();
      const fullURL = `${baseURL}/history/search`;

      // Default search parameters
      const params = {
        q: searchRequest.q || '',
        limit: searchRequest.limit || 20,
        agent_id: searchRequest.agent_id || undefined,
      };

      // Send GET request to history search endpoint
      const response = await axios.get<ChatHistorySearchResponse>(fullURL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        params,
        timeout: 15000, // 15 second timeout for search
      });
      // Update collection with search results
      this.items = [];
      const sessions = response.data.sessions || [];
      for (const session of sessions) {
        this.add(session);
      }

      // Store search metadata
      this.lastSearchQuery = params.q || '';
      this.totalCount = response.data.total_count || 0;

      return response.data;
    } catch (error: any) {
      // Provide detailed error information
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data as ChatHistoryApiError;
        const message = errorData?.message || error.response.statusText || 'Unknown API error';
        throw new Error(`Chat History Search Error: ${status} - ${message}`);
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the chat history API');
      } else {
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }

  /**
   * Get full conversation history for a specific session
   *
   * @param sessionId - The session ID to retrieve
   * @returns Promise<ChatSessionDetailResponse> - The full conversation
   */
  public async getSessionHistory(sessionId: string): Promise<ChatSessionDetailResponse> {
    try {
      // Check cache first
      if (this.cache.has(sessionId)) {
        return this.cache.get(sessionId)!;
      }

      const token = this.getAuthToken();
      const baseURL = this.getBaseURL();
      const fullURL = `${baseURL}/sessions/${sessionId}`;
      // Send GET request to session detail endpoint
      const response = await axios.get<ChatSessionDetailResponse>(fullURL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout for session detail
      });
      // Cache the result
      this.cache.set(sessionId, response.data);

      return response.data;
    } catch (error: any) {
      // Provide detailed error information
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data as ChatHistoryApiError;
        const message = errorData?.message || error.response.statusText || 'Unknown API error';
        throw new Error(`Session History Error: ${status} - ${message}`);
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the session history API');
      } else {
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }

  /**
   * Update the title of a specific chat session
   *
   * @param sessionId - The session ID to update
   * @param newTitle - The new title for the session
   * @returns Promise<ChatSession> - The updated session
   */
  public async updateSessionTitle(sessionId: string, newTitle: string): Promise<ChatSession> {
    try {
      const token = this.getAuthToken();
      const baseURL = this.getBaseURL();
      const fullURL = `${baseURL}/sessions/${sessionId}`;

      // Send PATCH request to update session title
      const response = await axios.patch<ChatSession>(fullURL,
        { title: newTitle },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        },
      );

      // Update local collection if session exists
      const sessionIndex = this.items.findIndex((session) => session.session_id === sessionId);
      if (sessionIndex !== -1) {
        this.items[sessionIndex] = response.data;
      }

      // Refresh cache for this session
      this.refreshSessionCache(sessionId);

      return response.data;
    } catch (error: any) {

      // Provide detailed error information
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data as ChatHistoryApiError;
        const message = errorData?.message || error.response.statusText || 'Unknown API error';
        throw new Error(`Update Session Title Error: ${status} - ${message}`);
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the session update API');
      } else {
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }

  /**
   * Delete a specific chat session
   *
   * @param sessionId - The session ID to delete
   * @returns Promise<boolean> - Success status
   */
  public async deleteSession(sessionId: string): Promise<boolean> {
    try {
      const token = this.getAuthToken();
      const baseURL = this.getBaseURL();
      const fullURL = `${baseURL}/sessions/${sessionId}`;
      // Send DELETE request to session endpoint
      await axios.delete(fullURL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });
      // Remove from cache and local collection
      this.cache.delete(sessionId);
      this.items = this.items.filter((session) => session.session_id !== sessionId);

      return true;
    } catch (error: any) {

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data as ChatHistoryApiError;
        const message = errorData?.message || error.response.statusText || 'Unknown API error';
        throw new Error(`Delete Session Error: ${status} - ${message}`);
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the delete API');
      } else {
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }

  /**
   * Clear all chat history
   *
   * @returns Promise<boolean> - Success status
   */
  public async clearAllHistory(): Promise<boolean> {
    try {
      const token = this.getAuthToken();
      const baseURL = this.getBaseURL();
      const fullURL = `${baseURL}/history/clear`;
      // Send DELETE request to clear all history
      await axios.delete(fullURL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 15000,
      });
      // Clear local cache and collection
      this.cache.clear();
      this.items = [];
      this.totalCount = 0;

      return true;
    } catch (error: any) {

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data as ChatHistoryApiError;
        const message = errorData?.message || error.response.statusText || 'Unknown API error';
        throw new Error(`Clear History Error: ${status} - ${message}`);
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the clear history API');
      } else {
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }

  /**
   * Get sessions filtered by agent
   *
   * @param agentId - The agent ID to filter by
   * @returns ChatSession[] - Filtered sessions
   */
  public getSessionsByAgent(agentId: string): ChatSession[] {
    return this.items.filter((session) => session.agent_id === agentId);
  }

  /**
   * Get sessions grouped by date
   *
   * @returns Map<string, ChatSession[]> - Sessions grouped by date
   */
  public getSessionsByDate(): Map<string, ChatSession[]> {
    const grouped = new Map<string, ChatSession[]>();

    for (const session of this.items) {
      const date = session.created_at.split('T')[0]; // Get YYYY-MM-DD part
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(session);
    }

    return grouped;
  }

  /**
   * Clear the local cache
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get the last search query
   */
  public getLastSearchQuery(): string {
    return this.lastSearchQuery;
  }

  /**
   * Get the total count from last search
   */
  public getTotalCount(): number {
    return this.totalCount;
  }

  /**
   * Refresh cache entry for a session (call after new messages)
   */
  public refreshSessionCache(sessionId: string): void {
    this.cache.delete(sessionId);
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string {
    return getIdentityToken();
  }

  /**
   * Get base URL for Atlas API
   */
  private getBaseURL(): string {
    return process.env.VUE_APP_ATLAS_API_URL || 'http://localhost:8000';
  }
}
